import { useState, useEffect } from 'react';
import { picksAPI } from '../services/api';

/**
 * Custom hook to manage user picks
 * @param {Array} matches - Array of match objects
 * @param {number} currentRound - Current round number
 * @returns {Object} Picks state and handlers
 */
export const usePicksManager = (matches, currentRound) => {
  const [picks, setPicks] = useState({});
  const [originalPicks, setOriginalPicks] = useState({});
  const [saving, setSaving] = useState(false);

  // Extract picks from matches when they change
  useEffect(() => {
    const existingPicks = {};
    matches.forEach((match) => {
      if (match.pick && match.pick.pickedTeamId) {
        existingPicks[match.id] = match.pick.pickedTeamId;
      }
    });
    setPicks(existingPicks);
    setOriginalPicks(JSON.parse(JSON.stringify(existingPicks)));
  }, [matches]);

  /**
   * Handle pick change
   * @param {number} matchId - Match ID
   * @param {number} teamId - Team ID
   */
  const handlePickChange = (matchId, teamId) => {
    setPicks((prev) => ({
      ...prev,
      [matchId]: teamId,
    }));
  };

  /**
   * Check if picks have changed from original
   * @returns {boolean} True if picks have changed
   */
  const hasPickChanges = () => {
    const currentPickKeys = Object.keys(picks);
    const originalPickKeys = Object.keys(originalPicks);

    if (currentPickKeys.length !== originalPickKeys.length) {
      return true;
    }

    return currentPickKeys.some((matchId) => picks[matchId] !== originalPicks[matchId]);
  };

  /**
   * Save all picks for the current round
   * @returns {Promise<void>}
   */
  const saveAllPicks = async () => {
    setSaving(true);
    try {
      // Format picks for API
      const picksArray = Object.entries(picks).map(([matchId, teamId]) => ({
        matchId: parseInt(matchId),
        pickedTeamId: parseInt(teamId),
        isDraw: false,
      }));

      await picksAPI.saveRoundPicks(currentRound, picksArray);

      // Update original picks to reflect saved state
      setOriginalPicks({ ...picks });

      return { success: true };
    } catch (err) {
      throw err;
    } finally {
      setSaving(false);
    }
  };

  return {
    picks,
    originalPicks,
    saving,
    setPicks,
    setOriginalPicks,
    handlePickChange,
    hasPickChanges,
    saveAllPicks,
  };
};
